import React, {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CartItem } from '~/types/Cart';

export const CartActionContext = createContext<{
  addToCart: (item: CartItem) => void;
  editCart: (item: CartItem, index: number) => void;
  getCartItem: (id: number) => CartItem | undefined;
  getAllCartItems: () => CartItem[];
  resetCart: () => void;
}>({
  addToCart: () => {},
  editCart: () => {},
  getCartItem: () => undefined,
  getAllCartItems: () => [],
  resetCart: () => {},
});

export const CartStateContext = createContext<State>([]);

type State = CartItem[];

const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cart, setCart] = useState<CartItem[]>([]);

  const dependencies = useRef<State>(cart);
  dependencies.current = cart;

  const getCartItem = useCallback((index: number) => {
    const cart = dependencies.current;
    return cart[index];
  }, []);

  const getAllCartItems = useCallback(() => {
    const cart = dependencies.current;
    return cart;
  }, []);

  const addToCart = useCallback((item: CartItem) => {
    const cart = dependencies.current;
    if (item.quantity === 0) {
      return;
    }
    setCart([...cart, item]);
  }, []);

  const editCart = useCallback((item: CartItem, index: number) => {
    const cart = dependencies.current;
    if (item.quantity === 0) {
      cart.splice(index, 1);
    } else {
      cart[index] = item;
    }
    setCart([...cart]);
  }, []);

  const resetCart = useCallback(() => {
    setCart([]);
  }, []);

  const actionContextValue = useMemo(() => {
    return {
      addToCart,
      editCart,
      getCartItem,
      getAllCartItems,
      resetCart,
    };
  }, [addToCart, editCart, getCartItem, resetCart, getAllCartItems]);

  const stateContextValue = useMemo(() => {
    return cart;
  }, [cart]);

  return (
    <CartActionContext.Provider value={actionContextValue}>
      <CartStateContext.Provider value={stateContextValue}>
        {children}
      </CartStateContext.Provider>
    </CartActionContext.Provider>
  );
};

export default CartProvider;
